import * as c from './types';

import {
  ECustomFieldEntity,
  CustomFieldEntry
} from '@clean-code/types/custom-fields';

import { ProductionItemType } from './../../services/contracts/production.service';
import { createImmerReducer } from '@reducers/utils/createReducer';

export interface ProductionConfig {
  workOrderForProductionAdvancement?: boolean | null;
  workOrderForProductionAdvancementDesktop?: boolean | null;
  showDefectsCatalogue?: boolean | null;
  showDefectsCatalogueDesktop?: boolean | null;
  showOnlySkusWithNominalSpeed?: boolean | null;
  enableCalendarInProductionAdvancementForm?: boolean | null;
  stopDaysInTablet?: number | null;
  showOperatorsInProductionAdvancements?: boolean | null;
  showOperatorsInProductionAdvancementsDesktop?: boolean | null;
  showRawMaterialsInProductionAdvancements?: boolean | null;
  showReleaseDateInWorkOrders?: boolean | null;
  showActualDeliveryDateInWorkOrders?: boolean | null;
  showNominalSpeedInProductionAdvancements?: boolean | null;
  showNominalSpeedInProductionAdvancementsDesktop?: boolean | null;
  equipmentNameEn?: string;
  equipmentNameEs?: string;
  //This field is wrongly read at company level it depends on the machine
  //defaultProductionAdvancementType: 'correct' | 'total';
}

export interface ProductionState {
  loading: boolean;
  error: any;
  success: boolean;
  data: any[];
  createLoading: boolean;
  createError: any;
  createSuccess: boolean;
  createBasicProductionAdvancementLoading: boolean;
  createBasicProductionAdvancementError: any;
  createBasicProductionAdvancementSuccess: boolean;
  updateBasicProductionAdvancementLoading: boolean;
  updateBasicProductionAdvancementError: any;
  updateBasicProductionAdvancementSuccess: boolean;
  splitBasicProductionAdvancementLoading: boolean;
  splitBasicProductionAdvancementError: any;
  splitBasicProductionAdvancementSuccess: boolean;
  updateLoading: boolean;
  updateError: any;
  updateSuccess: boolean;
  deleteLoading: boolean;
  deleteError: any;
  deleteSuccess: boolean;
  faileWorkStepEntries: any[];
  createBasicProductionAdvancementBulkLoading: boolean;
  createBasicProductionAdvancementBulkError: any;
  createBasicProductionAdvancementBulkSuccess: boolean;
  failedBasicProductionAdvancementEntries: any[];
  createFullProductionAdvancementBulkLoading: boolean;
  createFullProductionAdvancementBulkError: any;
  createFullProductionAdvancementBulkSuccess: boolean;
  failedFullProductionAdvancementEntries: any[];
  customFields: {
    [ECustomFieldEntity.PRODUCTION_ADVANCEMENTS]: CustomFieldEntry[];
    [ECustomFieldEntity.PRODUCTION_BATCHES]: CustomFieldEntry[];
    [ECustomFieldEntity.WORK_ORDERS]: CustomFieldEntry[];
  };
  isCustomFieldLoading: boolean;
  productionConfig: ProductionConfig;
  isWorkOrderForProductionAdvancementLoading: boolean;
  isUpdateSettingsForProductionLoading: boolean;
}

const initialState: ProductionState = {
  loading: false,
  error: null,
  success: false,
  data: [],
  createLoading: false,
  createError: null,
  createSuccess: false,
  createBasicProductionAdvancementLoading: false,
  createBasicProductionAdvancementError: null,
  createBasicProductionAdvancementSuccess: false,
  updateBasicProductionAdvancementError: null,
  updateBasicProductionAdvancementSuccess: false,
  updateBasicProductionAdvancementLoading: false,
  splitBasicProductionAdvancementLoading: false,
  splitBasicProductionAdvancementError: null,
  splitBasicProductionAdvancementSuccess: false,
  updateLoading: false,
  updateError: null,
  updateSuccess: false,
  deleteLoading: false,
  deleteError: null,
  deleteSuccess: false,
  faileWorkStepEntries: [],
  createBasicProductionAdvancementBulkLoading: false,
  createBasicProductionAdvancementBulkError: null,
  createBasicProductionAdvancementBulkSuccess: false,
  failedBasicProductionAdvancementEntries: [],
  createFullProductionAdvancementBulkLoading: false,
  createFullProductionAdvancementBulkError: null,
  createFullProductionAdvancementBulkSuccess: false,
  failedFullProductionAdvancementEntries: [],
  customFields: {
    [ECustomFieldEntity.PRODUCTION_ADVANCEMENTS]: [],
    [ECustomFieldEntity.PRODUCTION_BATCHES]: [],
    [ECustomFieldEntity.WORK_ORDERS]: []
  },
  isCustomFieldLoading: false,
  productionConfig: {
    workOrderForProductionAdvancement: null,
    showDefectsCatalogue: null,
    showOnlySkusWithNominalSpeed: null,
    enableCalendarInProductionAdvancementForm: null,
    stopDaysInTablet: null,
    showOperatorsInProductionAdvancements: true,
    showRawMaterialsInProductionAdvancements: true,
    showReleaseDateInWorkOrders: null,
    showActualDeliveryDateInWorkOrders: null,
    showNominalSpeedInProductionAdvancements: null
  },
  isWorkOrderForProductionAdvancementLoading: false,
  isUpdateSettingsForProductionLoading: false
};

export default createImmerReducer(initialState, {
  [c.FETCH_PRODUCTION_DATA]: (draft: ProductionState) => {
    draft.loading = true;
  },
  [c.FETCH_PRODUCTION_DATA_SUCCESS]: (
    draft: ProductionState,
    { payload }: { payload?: ProductionItemType[] }
  ) => {
    draft.loading = false;
    draft.success = true;
    draft.data = payload ?? [];
  },

  [c.FETCH_PRODUCTION_DATA_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.loading = false;
    draft.error = payload;
  },

  [c.FETCH_BASIC_PRODUCTION_DATA]: (draft: ProductionState) => {
    draft.loading = true;
  },
  [c.FETCH_BASIC_PRODUCTION_DATA_SUCCESS]: (
    draft: ProductionState,
    { payload }: { payload?: ProductionItemType[] }
  ) => {
    draft.loading = false;
    draft.success = true;
    draft.data = payload ?? [];
  },

  [c.FETCH_BASIC_PRODUCTION_DATA_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.loading = false;
    draft.error = payload;
  },

  [c.CREATE_PRODUCTION_DATA]: (draft: ProductionState) => {
    draft.createSuccess = false;
    draft.createLoading = true;
  },
  [c.CREATE_PRODUCTION_DATA_SUCCESS]: (draft: ProductionState, { payload }) => {
    draft.data = [payload, ...draft.data];
    draft.createLoading = false;
    draft.createSuccess = true;
  },
  [c.CREATE_PRODUCTION_DATA_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.createLoading = false;
    draft.createError = payload;
  },

  [c.CREATE_PRODUCTION_DATA_BULK]: (draft: ProductionState) => {
    draft.createSuccess = false;
    draft.createLoading = true;
  },
  [c.CREATE_PRODUCTION_DATA_BULK_SUCCESS]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.data = [payload, ...draft.data];
    draft.createLoading = false;
    draft.createSuccess = true;
  },

  [c.CREATE_PRODUCTION_DATA_BULK_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.createLoading = false;
    draft.createError = payload;
    draft.faileWorkStepEntries = payload.response?.data?.details ?? [];
  },

  [c.DELETE_PRODUCTION_DATA]: (draft: ProductionState) => {
    draft.deleteLoading = true;
  },
  [c.DELETE_PRODUCTION_DATA_SUCCESS]: (draft: ProductionState) => {
    // draft.data = [payload, ...draft.data];
    draft.deleteLoading = false;
    draft.deleteSuccess = true;
  },
  [c.DELETE_PRODUCTION_DATA_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.deleteLoading = false;
    draft.deleteError = payload;
  },

  [c.UPDATE_PRODUCTION_DATA]: (draft: ProductionState) => {
    draft.updateSuccess = false;
    draft.updateLoading = true;
  },
  [c.UPDATE_PRODUCTION_DATA_SUCCESS]: (draft: ProductionState) => {
    // draft.data = [payload, ...draft.data];
    draft.updateLoading = false;
    draft.updateSuccess = true;
  },

  [c.UPDATE_PRODUCTION_DATA_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.updateLoading = false;
    draft.updateError = payload;
  },
  [c.CREATE_BASIC_PRODUCTION_ADVANCEMENT]: (draft: ProductionState) => {
    draft.createBasicProductionAdvancementSuccess = false;
    draft.createBasicProductionAdvancementLoading = true;
  },
  [c.CREATE_BASIC_PRODUCTION_ADVANCEMENT_SUCCESS]: (draft: ProductionState) => {
    draft.createBasicProductionAdvancementLoading = false;
    draft.createBasicProductionAdvancementSuccess = true;
  },

  [c.CREATE_BASIC_PRODUCTION_ADVANCEMENT_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.createBasicProductionAdvancementLoading = false;
    draft.createBasicProductionAdvancementError = payload;
  },
  [c.CREATE_BASIC_PRODUCTION_ADVANCEMENT_BULK]: (draft: ProductionState) => {
    draft.createBasicProductionAdvancementBulkSuccess = false;
    draft.createBasicProductionAdvancementBulkLoading = true;
    draft.failedBasicProductionAdvancementEntries = [];
  },
  [c.CREATE_BASIC_PRODUCTION_ADVANCEMENT_BULK_SUCCESS]: (
    draft: ProductionState
  ) => {
    draft.createBasicProductionAdvancementBulkLoading = false;
    draft.createBasicProductionAdvancementBulkSuccess = true;
  },

  [c.CREATE_BASIC_PRODUCTION_ADVANCEMENT_BULK_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.createBasicProductionAdvancementBulkLoading = false;
    draft.createBasicProductionAdvancementBulkError = payload;
    draft.createBasicProductionAdvancementBulkSuccess = false;
    draft.failedBasicProductionAdvancementEntries =
      payload.response?.data?.details ?? [];
  },
  [c.UPDATE_BASIC_PRODUCTION_ADVANCEMENT]: (draft: ProductionState) => {
    draft.updateBasicProductionAdvancementSuccess = false;
    draft.updateBasicProductionAdvancementLoading = true;
  },
  [c.UPDATE_BASIC_PRODUCTION_ADVANCEMENT_SUCCESS]: (draft: ProductionState) => {
    draft.updateBasicProductionAdvancementLoading = false;
    draft.updateBasicProductionAdvancementSuccess = true;
  },

  [c.UPDATE_BASIC_PRODUCTION_ADVANCEMENT_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.updateBasicProductionAdvancementLoading = false;
    draft.updateBasicProductionAdvancementError = payload;
  },
  [c.SPLIT_BASIC_PRODUCTION_ADVANCEMENT]: (draft: ProductionState) => {
    draft.splitBasicProductionAdvancementSuccess = false;
    draft.splitBasicProductionAdvancementLoading = true;
  },
  [c.SPLIT_BASIC_PRODUCTION_ADVANCEMENT_SUCCESS]: (draft: ProductionState) => {
    draft.splitBasicProductionAdvancementLoading = false;
    draft.splitBasicProductionAdvancementSuccess = true;
  },

  [c.SPLIT_BASIC_PRODUCTION_ADVANCEMENT_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.splitBasicProductionAdvancementLoading = false;
    draft.splitBasicProductionAdvancementError = payload;
  },
  [c.CREATE_FULL_PRODUCTION_ADVANCEMENT_BULK]: (draft: ProductionState) => {
    draft.createFullProductionAdvancementBulkSuccess = false;
    draft.createFullProductionAdvancementBulkLoading = true;
    draft.failedFullProductionAdvancementEntries = [];
  },
  [c.CREATE_FULL_PRODUCTION_ADVANCEMENT_BULK_SUCCESS]: (
    draft: ProductionState,
    { payload }
  ) => {
    draft.data = [payload, ...draft.data];
    draft.createFullProductionAdvancementBulkLoading = false;
    draft.createFullProductionAdvancementBulkSuccess = true;
  },

  [c.CREATE_FULL_PRODUCTION_ADVANCEMENT_BULK_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.createFullProductionAdvancementBulkLoading = false;
    draft.createFullProductionAdvancementBulkError = payload;
    draft.failedFullProductionAdvancementEntries =
      payload.response?.data?.details ?? [];
  },
  [c.RESET_CREATE_BASIC_PRODUCTION_ADVANCEMENT_BULK_SUCCESS]: (
    draft: ProductionState
  ) => {
    draft.createBasicProductionAdvancementBulkSuccess = false;
  },
  [c.FETCH_CUSTOM_FIELDS]: (draft: ProductionState) => {
    draft.isCustomFieldLoading = true;
  },
  [c.FETCH_CUSTOM_FIELDS_SUCCESS]: (
    draft: ProductionState,
    {
      payload
    }: {
      payload?: { entity: ECustomFieldEntity; value: CustomFieldEntry[] };
    }
  ) => {
    const newCustomFields = {
      ...draft.customFields,
      ...(payload ? { [payload.entity]: payload.value } : {})
    };
    draft.isCustomFieldLoading = false;
    draft.customFields = newCustomFields;
  },
  [c.FETCH_CUSTOM_FIELDS_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.isCustomFieldLoading = false;
    draft.error = payload;
  },
  [c.FETCH_SETTINGS_FOR_PRODUCTION]: (draft: ProductionState) => {
    draft.isWorkOrderForProductionAdvancementLoading = true;
  },
  [c.FETCH_SETTINGS_FOR_PRODUCTION_SUCCESS]: (
    draft: ProductionState,
    { payload }
  ) => {
    draft.productionConfig = { ...draft.productionConfig, ...payload };
    draft.isWorkOrderForProductionAdvancementLoading = false;
  },
  [c.FETCH_SETTINGS_FOR_PRODUCTION_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.error = payload;
    draft.isWorkOrderForProductionAdvancementLoading = false;
  },
  [c.UPDATE_SETTINGS_FOR_PRODUCTION]: (draft: ProductionState) => {
    draft.isUpdateSettingsForProductionLoading = true;
  },
  [c.UPDATE_SETTINGS_FOR_PRODUCTION_SUCCESS]: (
    draft: ProductionState,
    { payload }
  ) => {
    draft.isUpdateSettingsForProductionLoading = false;
    draft.productionConfig = payload;
  },
  [c.UPDATE_SETTINGS_FOR_PRODUCTION_ERROR]: (
    draft: ProductionState,
    { payload }: { payload?: any }
  ) => {
    draft.error = payload;
    draft.isUpdateSettingsForProductionLoading = false;
  }
});
